import { ArrowForward, AutoAwesomeOutlined, FavoriteBorder, LanguageOutlined, RocketLaunchOutlined } from '@mui/icons-material';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useThemeContext } from '../global/ThemeContext';
import useTranslations from '../global/translation/hooks/translation';
import { designTokens } from '../../theme/designTokens';

const AbeoFeature = () => {
  const { currentLanguage, translations } = useTranslations();
  const { darkMode } = useThemeContext();
  const palette = darkMode ? designTokens.colors.dark : designTokens.colors.light;
  const benefits = [
    { icon: <FavoriteBorder />, text: translations.label.abeoBenefitOne[currentLanguage] },
    { icon: <LanguageOutlined />, text: translations.label.abeoBenefitTwo[currentLanguage] },
    { icon: <RocketLaunchOutlined />, text: translations.label.abeoBenefitThree[currentLanguage] },
    { icon: <AutoAwesomeOutlined />, text: translations.label.abeoBenefitFour[currentLanguage] },
  ];

  return (
    <Box
      sx={{
        my: { xs: 6, md: 10 },
        borderRadius: { xs: 0, md: designTokens.radius.lg },
        overflow: 'hidden',
        bgcolor: darkMode ? palette.background.elevated : '#FFFFFF',
        border: `1px solid ${palette.border.subtle}`,
        boxShadow: darkMode ? designTokens.shadows.elevatedDark : designTokens.shadows.elevated,
      }}
    >
      <Grid container>
        <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
          <Box sx={{ p: { xs: 3.5, sm: 5, md: 7 } }}>
            <Typography
              component="p"
              sx={{
                color: palette.primary,
                fontSize: '0.76rem',
                fontWeight: 800,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              {translations.label.abeoSectionEyebrow[currentLanguage]}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: palette.text.primary,
                fontSize: { xs: '2.25rem', md: '3.35rem' },
                lineHeight: 1.08,
                letterSpacing: '-0.04em',
                maxWidth: '780px',
              }}
            >
              {translations.label.abeoSectionTitle[currentLanguage]}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: palette.text.secondary, lineHeight: 1.8, maxWidth: '760px', mt: 3, fontSize: '1rem' }}
            >
              {translations.label.abeoSectionDescription[currentLanguage]}
            </Typography>

            <Grid container spacing={1.5} sx={{ mt: 3 }}>
              {benefits.map((benefit) => (
                <Grid item xs={12} sm={6} key={benefit.text}>
                  <Stack
                    direction="row"
                    spacing={1.2}
                    alignItems="flex-start"
                    sx={{
                      height: '100%',
                      p: 2,
                      borderRadius: designTokens.radius.md,
                      bgcolor: darkMode ? palette.background.subtle : '#F4F7FD',
                      color: palette.text.primary,
                    }}
                  >
                    <Box sx={{ display: 'flex', color: palette.primary, '& svg': { fontSize: 21 } }}>{benefit.icon}</Box>
                    <Typography variant="body2" sx={{ lineHeight: 1.55, fontWeight: 600 }}>
                      {benefit.text}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            <Button
              variant="contained"
              size="large"
              href="https://aboe.urbanequestrian.pl/"
              endIcon={<ArrowForward />}
              sx={{ mt: 4, px: 3.5, py: 1.55 }}
            >
              {translations.label.abeoSectionCta[currentLanguage]}
            </Button>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1.6, color: palette.text.tertiary, fontStyle: 'italic', maxWidth: '620px' }}
            >
              {translations.label.abeoEnglishOnlyNote[currentLanguage]}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={5} sx={{ display: 'flex', order: { xs: 1, md: 2 } }}>
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 300, sm: 340, md: '100%' },
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              overflow: 'hidden',
              p: { xs: 4, md: 5 },
              background: darkMode
                ? 'linear-gradient(145deg, #102C5B 0%, #07142B 100%)'
                : 'linear-gradient(145deg, #2E63C8 0%, #0B2B67 100%)',
              color: '#FFFFFF',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: 330,
                height: 330,
                border: '1px solid rgba(255,255,255,0.22)',
                borderRadius: '50%',
                right: -150,
                top: -130,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                width: 180,
                height: 180,
                border: '1px solid rgba(255,255,255,0.16)',
                borderRadius: '50%',
                left: -80,
                bottom: -65,
              },
            }}
          >
            <Typography sx={{ position: 'relative', zIndex: 1, fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.78 }}>
              {translations.label.abeoCardLocation[currentLanguage]}
            </Typography>
            <Box sx={{ position: 'relative', zIndex: 1, my: 5 }}>
              <Typography
                sx={{
                  fontSize: { xs: '3.4rem', md: '4.5rem' },
                  fontWeight: 500,
                  lineHeight: 0.96,
                  letterSpacing: '-0.055em',
                }}
              >
                A Bit of
                <br />
                Everything
              </Typography>
            </Box>
            <Typography sx={{ position: 'relative', zIndex: 1, maxWidth: 320, fontSize: '0.9rem', lineHeight: 1.65, opacity: 0.76 }}>
              {translations.label.abeoCardTopics[currentLanguage]}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AbeoFeature;
